import { AdvertisedRateBucket, AdvertisedRateDisclosure, Rate } from '../shared/rate.model';

export interface AdvertisedRateSignals {
  type?: any;
  loanPurpose?: any;
  formType?: any;
  lpButton?: any;
  pageType?: any;
  program?: any;
}

export interface EffectiveRateRequest {
  kind: 'generic' | 'scoped' | 'invalid';
  bucket: AdvertisedRateBucket | null;
}

export const DEFAULT_EFFECTIVE_RATE_BUCKET: AdvertisedRateBucket = {
  purpose: 'refinance',
  product: '10yr_fixed',
};

const GENERIC_SIGNALS = ['', 'airatefinder', 'ai', 'default', 'default-2', 'today', 'calculator', 'mortgage-calculator', 'creditunion', 'credit-union'];
const INVALID_PRODUCT = '__invalid__';
const REFINANCE_SUBTYPES = ['cashout', 'equity', 'homeimprovement', 'heloc_exact', 'home_equity_exact'];

function signal(value: any): string {
  return (value == null ? '' : String(value)).toLowerCase().trim();
}

function normalizedProduct(raw: string, source: 'base' | 'form' | 'button' | 'page'): string | null {
  if (GENERIC_SIGNALS.indexOf(raw) >= 0 && source !== 'button') { return ''; }
  if (['refinance', 'refi', 'harp'].indexOf(raw) >= 0) { return 'refinance'; }
  if (['purchase', 'home-purchase'].indexOf(raw) >= 0) { return 'purchase'; }
  if (['cashout', 'cash-out'].indexOf(raw) >= 0) { return 'cashout'; }
  if (['homeimprovement', 'home-improvement', 'home-improvement-loan'].indexOf(raw) >= 0) {
    return 'homeimprovement';
  }
  if (raw === 'home_equity') { return 'home_equity_exact'; }
  if (raw === 'heloc') { return source === 'button' || source === 'page' ? 'heloc_exact' : 'equity'; }
  if (['homeequity', 'home-equity', 'home-equity-loan', 'home-equity-options', 'equity-calculator'].indexOf(raw) >= 0) {
    return 'equity';
  }
  return null;
}

function mergeRefinanceContext(selected: string, incoming: string): string {
  if (!selected) { return incoming; }
  if (!incoming || selected === incoming) { return selected; }
  if (selected === 'refinance' && REFINANCE_SUBTYPES.indexOf(incoming) >= 0) { return incoming; }
  if (incoming === 'refinance' && REFINANCE_SUBTYPES.indexOf(selected) >= 0) { return selected; }
  if (selected === 'equity' && ['heloc_exact', 'home_equity_exact'].indexOf(incoming) >= 0) { return incoming; }
  if (incoming === 'equity' && ['heloc_exact', 'home_equity_exact'].indexOf(selected) >= 0) { return selected; }
  return INVALID_PRODUCT;
}

function mergePageContext(selected: string, fromPage: string): string {
  if (!fromPage) { return selected; }
  if (!selected || selected === fromPage) { return fromPage; }
  if (['heloc_exact', 'home_equity_exact'].indexOf(fromPage) >= 0
    && (selected === 'refinance' || selected === 'equity')) {
    return fromPage;
  }
  if (fromPage === 'equity' && ['heloc_exact', 'home_equity_exact'].indexOf(selected) >= 0) {
    return selected;
  }
  return INVALID_PRODUCT;
}

// The original form's phone step resolves through this function and keeps the composed keys it has always asked for:
// a purchase entry there asks purchase.10yr_fixed, which is never published, so its closing line shows no rate. Moving the
// original form onto the published buckets puts a rate into that line, a visible change to the original form, so it waits
// for Ed. Hope resolves through resolveEffectiveRateRequest, which asks only for published buckets (FRU-23775).
export function resolveEffectiveRateBucket(
  signals: AdvertisedRateSignals,
  defaultWhenUnresolved: boolean = false,
): AdvertisedRateBucket | null {
  return composeEffectiveRateBucket(signals, defaultWhenUnresolved, false);
}

function composeEffectiveRateBucket(
  signals: AdvertisedRateSignals,
  defaultWhenUnresolved: boolean,
  publishedOnly: boolean,
): AdvertisedRateBucket | null {
  signals = signals || {};
  const rawProgram = signal(signals.program);
  if (rawProgram && ['conventional', 'fha', 'va'].indexOf(rawProgram) < 0) { return null; }
  const programs = [rawProgram === 'conventional' ? '' : rawProgram];
  let selected = '';
  const consume = (raw: string, source: 'base' | 'form' | 'button'): boolean => {
    if (!raw && source !== 'button') { return true; }
    if (raw === 'fha' || raw === 'fha-loans') { programs.push('fha'); return true; }
    if (raw === 'va' || raw === 'veteran-active-military') { programs.push('va'); return true; }
    const product = normalizedProduct(raw, source);
    if (product === null) { return false; }
    selected = mergeRefinanceContext(selected, product);
    return selected !== INVALID_PRODUCT;
  };
  if (!consume(signal(signals.type), 'base')
    || !consume(signal(signals.loanPurpose), 'base')
    || !consume(signal(signals.formType), 'form')
    || (signal(signals.lpButton) !== '' && !consume(signal(signals.lpButton), 'button'))) {
    return null;
  }

  const pageType = signal(signals.pageType);
  if (pageType === 'fha' || pageType === 'fha-loans') { programs.push('fha'); }
  else if (pageType === 'va' || pageType === 'veteran-active-military') { programs.push('va'); }
  else {
    const fromPage = normalizedProduct(pageType, 'page');
    if (fromPage === null) { return null; }
    selected = mergePageContext(selected, fromPage);
    if (selected === INVALID_PRODUCT) { return null; }
  }

  const concretePrograms = programs.filter(Boolean);
  if (concretePrograms.length > 1 && concretePrograms.some(value => value !== concretePrograms[0])) { return null; }
  const program = concretePrograms[0] || '';
  if (!selected && !program) { return defaultWhenUnresolved ? DEFAULT_EFFECTIVE_RATE_BUCKET : null; }

  // Government mortgage identity and an explicit HELOC/home-equity product
  // cannot describe the same requested offer. Never discard either signal.
  if (program && (selected === 'heloc_exact' || selected === 'home_equity_exact')) {
    return null;
  }

  if (selected === 'heloc_exact') { return { purpose: 'heloc', product: 'heloc' }; }
  if (selected === 'home_equity_exact') { return { purpose: 'home_equity', product: 'home_equity' }; }

  let purpose: 'purchase' | 'refinance' | 'cash_out';
  if (selected === 'purchase') { purpose = 'purchase'; }
  else if (['equity', 'cashout', 'homeimprovement'].indexOf(selected) >= 0) { purpose = 'cash_out'; }
  else if (selected === 'refinance') { purpose = 'refinance'; }
  else if (program === 'va') { purpose = 'cash_out'; }
  else if (program === 'fha') { purpose = 'refinance'; }
  else { return null; }

  if (publishedOnly) { return publishedRateBucket(purpose, program); }
  if (program === 'fha' || program === 'va') { return { purpose, product: `${program}_15yr` }; }
  return { purpose, product: '10yr_fixed' };
}

/**
 * FRU-23775 (10/4), Hope entries: only buckets the reviewed revision publishes. A bucket publishes only when an active landing page
 * puts it in verification scope, and five composed keys had none, so those openings asked for a bucket that never answers:
 * purchase.10yr_fixed, purchase.va_15yr, purchase.fha_15yr, cash_out.fha_15yr and refinance.va_15yr. Each now resolves to a
 * published bucket of the SAME purpose and program, or to no rate. Never a different program or purpose:
 *   purchase            -> purchase.15yr_fixed (the shortest published purchase term, where the "as low as" floor sits)
 *   purchase VA         -> purchase.va (priced as a 30-year VA purchase)
 *   purchase FHA        -> no rate (no purchase FHA is published)
 *   cash-out FHA        -> no rate (no cash-out FHA is published)
 *   refinance VA        -> no rate (no VA rate-and-term is published; the VA cash-out pair would need its own wording)
 * Unchanged: refinance and cash-out 10-year fixed, refinance FHA 15-year, and VA with no other purpose (cash-out VA 15-year).
 */
function publishedRateBucket(purpose: 'purchase' | 'refinance' | 'cash_out', program: string): AdvertisedRateBucket | null {
  if (program === 'fha') { return purpose === 'refinance' ? { purpose, product: 'fha_15yr' } : null; }
  if (program === 'va') {
    if (purpose === 'purchase') { return { purpose, product: 'va' }; }
    return purpose === 'cash_out' ? { purpose, product: 'va_15yr' } : null;
  }
  return purpose === 'purchase' ? { purpose, product: '15yr_fixed' } : { purpose, product: '10yr_fixed' };
}

// FRU-22065: callers need to distinguish an honestly generic opening from an invalid or
// contradictory set of product signals. Both resolve to null without a default, but only the
// generic opening may use the canonical unscoped/default-rate UX. A scoped request always carries
// its exact bucket through to the adapter; an invalid request must not be converted into one.
export function resolveEffectiveRateRequest(signals: AdvertisedRateSignals): EffectiveRateRequest {
  const scoped = composeEffectiveRateBucket(signals, false, true);
  if (scoped) { return { kind: 'scoped', bucket: scoped }; }
  return composeEffectiveRateBucket(signals, true, true)
    ? { kind: 'generic', bucket: null }
    : { kind: 'invalid', bucket: null };
}

// The old single-rate adapter may use its browser-local last-known pair when the effective adapter
// is rolled back. Effective payloads cannot: the local value has no revision, disclosure, or
// rates-checked provenance and is user-modifiable.
// Ed 8/13: "heloc page we also use the comparison." Every equity entry -- however the visitor
// worded the search -- is QUOTED with the fixed home-equity comparison row the ads promise, with the
// HELOC named as one of the options being compared.
//
// This is a PRESENTATION policy, so it deliberately does NOT live in resolveEffectiveRateBucket.
// hopex review 8/13 caught why that distinction matters: the resolver is shared with the original
// form, whose phone step pairs the rate with its own product label. On a HELOC entry that step sets
// typeLabel 'Home equity line' and builds "Get your best rates and options as low as {RATE}" -- so
// remapping the bucket globally would have printed the fixed cash-out number under a HELOC label,
// which is exactly the product relabeling Hope's opener is written to avoid. Hope's opener earns the
// number by naming it as a comparison ("Fixed! Compare Mortgage, Home Equity & HELOC options"); the
// original form makes no such statement, so it keeps asking for the honest exact bucket.
//
// Side effect worth knowing: both exact equity buckets are refused by validatedEffectiveRate at the
// `offer` gate (a line-of-credit offer legitimately carries existing_mortgage_balance, and
// heloc.heloc adds null monthly_payment / eight_year_cost), so the original form continues to show
// no number on those entries -- unchanged from today, and a separate decision.
export const EQUITY_COMPARISON_BUCKET: AdvertisedRateBucket = { purpose: 'cash_out', product: '10yr_fixed' };

const EXACT_EQUITY_BUCKET_KEYS = ['heloc.heloc', 'home_equity.home_equity'];

export function applyEquityComparisonPolicy(request: EffectiveRateRequest): EffectiveRateRequest {
  if (!request || request.kind !== 'scoped' || !request.bucket) { return request; }
  if (EXACT_EQUITY_BUCKET_KEYS.indexOf(rateBucketKey(request.bucket)) < 0) { return request; }
  return { kind: 'scoped', bucket: EQUITY_COMPARISON_BUCKET };
}

export function allowsLastKnownRateFloor(effectiveRatesEnabled: boolean): boolean {
  return !effectiveRatesEnabled;
}

export function rateBucketKey(bucket: AdvertisedRateBucket): string {
  return `${bucket.purpose}.${bucket.product}`;
}

// Ed 8/4: scenario AMOUNTS are the rates team's to tune (925k -> 950k tonight broke the hardcoded
// version and stripped every rate line on the site). Lock the identity anchors (PA scenario, tier
// family format); amounts must be canonical whole numbers and -- the real compliance invariant --
// IDENTICAL between the offer and its own payload's scenario (checked in exactPublicOffer).
function canonicalWholeNumber(value: any): boolean {
  if (typeof value === 'number') { return Number.isSafeInteger(value) && value > 0; }
  return typeof value === 'string' && /^[1-9]\d*$/.test(value);
}

function canonicalWholeNumberOrZero(value: any): boolean {
  if (typeof value === 'number') { return Number.isSafeInteger(value) && value >= 0; }
  return typeof value === 'string' && /^(?:0|[1-9]\d*)$/.test(value);
}

function lockedScenario(scenario: any): boolean {
  return !!scenario
    && exactKeys(scenario, [
      'state', 'zip', 'property_value', 'loan_amount', 'cash_out_amount', 'credit_score', 'credit_tier',
    ])
    && scenario.state === 'PA'
    && typeof scenario.zip === 'string'
    && /^\d{5}$/.test(scenario.zip)
    && canonicalWholeNumber(scenario.property_value)
    && canonicalWholeNumber(scenario.loan_amount)
    && canonicalWholeNumberOrZero(scenario.cash_out_amount)
    && canonicalWholeNumberMatches(scenario.credit_score, String(scenario.credit_score).trim())
    && typeof scenario.credit_tier === 'string'
    && /^\d{3}-\d{3}$|^\d{3}\+$/.test(scenario.credit_tier);
}

// Ed 8/4: the OFFER figures are the live rates (mktgx owns them); the envelope's scenario block
// can lag a rebuild. Drift between them is WORTH A WARN (envelope hygiene for the rates owner)
// but must NEVER refuse to display our own live rates.
function warnOnScenarioDrift(offer: any, payload: any): void {
  try {
    const sc = (payload && payload.scenario) || {};
    const drift: string[] = [];
    ['state', 'zip', 'credit_score', 'credit_tier', 'loan_amount', 'property_value'].forEach(f => {
      if (String(offer[f]) !== String(sc[f])) { drift.push(f + '(' + String(sc[f]) + '->' + String(offer[f]) + ')'); }
    });
    if (drift.length) {
      console.warn('[effective-rate] offer/scenario envelope drift (displaying the offer, the live rate):', drift.join(' '));
    }
  } catch (e) { /* non-fatal */ }
}

// P0 8/6 (Ed's Google-meeting blank): exact key-set equality treated an ADDITIVE upstream field
// (rates_checked, added by the rates owner as a daily verified stamp) as corruption and blanked
// every default page. Doctrine: unknown EXTRA keys are envelope drift -- warn, never refuse;
// MISSING required keys are genuine shape corruption and still reject.
function exactKeys(value: any, keys: string[]): boolean {
  return !!value
    && typeof value === 'object'
    && !Array.isArray(value)
    && keys.every(key => Object.prototype.hasOwnProperty.call(value, key));
}
function unknownKeys(value: any, keys: string[]): string[] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) { return []; }
  return Object.keys(value).filter(k => keys.indexOf(k) < 0);
}

function canonicalWholeNumberMatches(value: any, expected: string): boolean {
  if (typeof value === 'number') {
    return Number.isSafeInteger(value) && value >= 0 && String(value) === expected;
  }
  return typeof value === 'string' && /^(?:0|[1-9]\d*)$/.test(value) && value === expected;
}

function decimalString(value: any, allowNegative: boolean = false): value is string {
  if (typeof value !== 'string') { return false; }
  const pattern = allowNegative ? /^-?(?:0|[1-9]\d*)(?:\.\d+)?$/ : /^(?:0|[1-9]\d*)(?:\.\d+)?$/;
  return pattern.test(value);
}

function positiveDecimalString(value: any): value is string {
  return decimalString(value) && /[1-9]/.test(value);
}

function sha256(value: any): value is string {
  return typeof value === 'string' && /^[a-f0-9]{64}$/.test(value);
}

// FRU-25099: exported for Hope's rate-integrity marker; behaviour unchanged.
export function strictIsoEpochSeconds(value: any): number | undefined {
  if (typeof value !== 'string') { return undefined; }
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?(Z|[+-]\d{2}:\d{2})$/.exec(value);
  if (!match) { return undefined; }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6]);
  const millisecond = Number((match[7] || '').padEnd(3, '0'));
  const zone = match[8];
  if (month < 1 || month > 12 || day < 1 || day > 31
    || hour > 23 || minute > 59 || second > 59) {
    return undefined;
  }
  if (zone !== 'Z') {
    const offsetHour = Number(zone.slice(1, 3));
    const offsetMinute = Number(zone.slice(4, 6));
    if (offsetHour > 14 || offsetMinute > 59 || (offsetHour === 14 && offsetMinute !== 0)) {
      return undefined;
    }
  }

  const calendar = new Date(0);
  calendar.setUTCFullYear(year, month - 1, day);
  calendar.setUTCHours(hour, minute, second, millisecond);
  if (calendar.getUTCFullYear() !== year
    || calendar.getUTCMonth() !== month - 1
    || calendar.getUTCDate() !== day
    || calendar.getUTCHours() !== hour
    || calendar.getUTCMinutes() !== minute
    || calendar.getUTCSeconds() !== second
    || calendar.getUTCMilliseconds() !== millisecond) {
    return undefined;
  }

  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? Math.floor(parsed / 1000) : undefined;
}

function compactPercent(value: string): string | null {
  if (!positiveDecimalString(value)) { return null; }
  const parts = value.split('.', 2);
  return `${parts[0]}.${parts[1] ? parts[1][0] : '0'}`;
}

function expectedTermMonths(product: string): number | null {
  // The published VA purchase product carries no term in its name; the reviewed revision prices it as a 30-year loan.
  if (product === 'va') { return 360; }
  const match = product.match(/(?:^|_)(10|15|20|30)yr(?:_|$)/);
  return match ? Number(match[1]) * 12 : null;
}

/** FRU-24222: how a caller validates a reviewed payload. lineOfCredit lets a heloc.heloc offer through with the fields a line of
 *  credit legitimately has (an existing_mortgage_balance; no points, monthly_payment or eight_year_cost). Only Hope asks for it,
 *  on its HELOC entries; every other caller, the original form included, validates exactly as before. */
export interface EffectiveRateValidation {
  lineOfCredit?: boolean;
}

function lineOfCreditOffer(bucket: AdvertisedRateBucket, opts?: EffectiveRateValidation): boolean {
  return !!(opts && opts.lineOfCredit) && !!bucket && bucket.purpose === 'heloc' && bucket.product === 'heloc';
}

function exactPublicOffer(offer: any, payload: any, bucket: AdvertisedRateBucket, lineOfCredit: boolean = false): boolean {
  if (!offer || typeof offer !== 'object' || Array.isArray(offer)) { return false; }
  const allowedKeys = [
    'rate', 'apr', 'points', 'monthly_payment', 'upfront_costs', 'eight_year_cost',
    'purpose', 'product_key', 'term_months', 'state', 'zip', 'credit_score', 'credit_tier',
    'loan_amount', 'property_value', 'cash_out_amount', 'captured_at_et', 'fees',
  ].concat(lineOfCredit ? ['existing_mortgage_balance'] : []);
  if (Object.keys(offer).some(key => allowedKeys.indexOf(key) < 0)
    || allowedKeys.some(key => !Object.prototype.hasOwnProperty.call(offer, key))) {
    return false;
  }
  const termMonths = expectedTermMonths(bucket.product);
  if (offer.purpose !== bucket.purpose
    || offer.product_key !== bucket.product
    || offer.term_months !== termMonths
    || !positiveDecimalString(offer.rate)
    || !positiveDecimalString(offer.apr)
    || !(lineOfCredit && offer.points === null ? true : decimalString(offer.points, true))
    || !(lineOfCredit && offer.monthly_payment === null ? true : positiveDecimalString(offer.monthly_payment))
    || !decimalString(offer.upfront_costs)
    || !(lineOfCredit && offer.eight_year_cost === null ? true : positiveDecimalString(offer.eight_year_cost))
    || (lineOfCredit && !canonicalWholeNumberOrZero(offer.existing_mortgage_balance))
    || offer.state !== 'PA'
    || typeof offer.zip !== 'string' || !/^\d{5}$/.test(String(offer.zip))
    || !canonicalWholeNumberMatches(offer.credit_score, String(offer.credit_score).trim())
    || typeof offer.credit_tier !== 'string' || !/^\d{3}-\d{3}$|^\d{3}\+$/.test(String(offer.credit_tier))
    || !canonicalWholeNumber(offer.loan_amount)
    || !canonicalWholeNumber(offer.property_value)
    || !canonicalWholeNumberOrZero(offer.cash_out_amount)
    || (bucket.purpose !== 'cash_out' && String(offer.cash_out_amount) !== '0')
    || typeof offer.captured_at_et !== 'string'
    || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:-04:00|-05:00)$/.test(offer.captured_at_et)
    || offer.rate !== payload.rate
    || offer.apr !== payload.apr
    || offer.points !== payload.points
    || !Array.isArray(offer.fees)) {
    return false;
  }
  return offer.fees.every((fee: any) => fee
    && typeof fee === 'object'
    && !Array.isArray(fee)
    && Object.keys(fee).length === 3
    && typeof fee.description === 'string'
    && fee.description.trim() !== ''
    && decimalString(fee.value, true)
    && typeof fee.required === 'boolean');
}

function expectedDisclosureType(bucket: AdvertisedRateBucket): AdvertisedRateDisclosure['type'] {
  if (bucket.product.startsWith('fha')) { return 'fha'; }
  if (bucket.product.startsWith('va')) { return 'va'; }
  if (['cash_out', 'heloc', 'home_equity'].indexOf(bucket.purpose) >= 0) { return 'home_equity'; }
  return 'default';
}

function exactDisclosure(
  disclosure: any,
  payload: any,
  bucket: AdvertisedRateBucket,
  expectedOrigin: string,
): boolean {
  const keys = [
    'type', 'option', 'url', 'context', 'version_id', 'content_sha256',
    'bundle_resource_id', 'bundle_sha256', 'public_offer_sha256',
  ];
  if (!exactKeys(disclosure, keys)) { return false; }
  try {
    const _dx = unknownKeys(disclosure, keys);
    if (_dx.length) { console.warn('[effective-rate] unknown additive disclosure keys (tolerated, never rendered):', _dx.join(',')); }
  } catch (e) { /* non-fatal */ }
  const type = expectedDisclosureType(bucket);
  const options: { [key: string]: string } = {
    default: 'advertising_disclosure_html',
    home_equity: 'advertising_disclosure_html_home_equity',
    fha: 'advertising_disclosure_html_fha',
    va: 'advertising_disclosure_html_va',
  };
  if (disclosure.type !== type
    || disclosure.option !== options[type]
    || typeof disclosure.context !== 'string'
    || !/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(disclosure.context)
    || disclosure.context.length > 2048
    || !Number.isSafeInteger(disclosure.version_id)
    || disclosure.version_id < 1
    || !sha256(disclosure.content_sha256)
    || typeof disclosure.bundle_resource_id !== 'string'
    || disclosure.bundle_resource_id.trim() === ''
    || disclosure.bundle_resource_id.length > 191
    || !sha256(disclosure.bundle_sha256)
    || !sha256(disclosure.public_offer_sha256)
    || disclosure.public_offer_sha256 !== payload.offer_sha256
    || typeof disclosure.url !== 'string') {
    return false;
  }
  try {
    const url = new URL(disclosure.url);
    const configuredOrigin = new URL(expectedOrigin);
    return url.protocol === 'https:'
      && configuredOrigin.protocol === 'https:'
      && url.origin === configuredOrigin.origin
      && url.port === ''
      && url.username === ''
      && url.password === ''
      && url.search === ''
      && url.hash === ''
      && url.pathname === `/advertised-rate-disclosure/${disclosure.context}`;
  } catch (e) {
    return false;
  }
}

// The edoffice rate endpoint still serves the legacy two-field preview contract. Production must
// never accept that unreviewed shape, but hiding the pair on the test bench prevents the actual
// one-bubble/green-rate presentation from being reviewed before deploy. Permit only the exact
// edoffice WWW host and only the exact {rate, apr} shape; every other host/payload stays on the
// revision-bound validator below.
export function edofficeLegacyRatePreview(payload: any, wwwOrigin: string): Rate | null {
  let exactEdofficeOrigin = false;
  try {
    const url = new URL(wwwOrigin);
    exactEdofficeOrigin = url.protocol === 'https:'
      && url.hostname.toLowerCase() === 'edoffice-www.freerateupdate.com'
      && url.port === ''
      && url.username === ''
      && url.password === ''
      && url.pathname === '/'
      && url.search === ''
      && url.hash === '';
  } catch (e) { return null; }
  if (!exactEdofficeOrigin
    || !payload || typeof payload !== 'object' || Array.isArray(payload)
    || Object.keys(payload).sort().join(',') !== 'apr,rate'
    || !positiveDecimalString(payload.rate)
    || !positiveDecimalString(payload.apr)) {
    return null;
  }
  const rate = compactPercent(payload.rate);
  const apr = compactPercent(payload.apr);
  if (rate === null || apr === null) { return null; }
  return {
    rate,
    apr,
    display_rate: rate,
    display_apr: apr,
    display_pair: `${rate}% ${apr} APR`,
    reason: 'edoffice_legacy_preview',
  };
}

export function validatedEffectiveRate(
  payload: any,
  bucket: AdvertisedRateBucket,
  expectedDisclosureOrigin: string,
  opts?: EffectiveRateValidation,
): Rate | null {
  const lineOfCredit = lineOfCreditOffer(bucket, opts);
  const topKeys = [
    'status', 'payload_version', 'payload_source', 'rate_set_name', 'revision_id', 'revision_sha256',
    'generated_at', 'generated_at_et', 'geo', 'scenario', 'purpose', 'product', 'rate', 'apr', 'points',
    'display_rate', 'display_apr', 'display_pair', 'offer_sha256', 'offer', 'disclosure', 'warnings', 'rates_checked',
  ];
  const displayRate = payload && typeof payload.rate === 'string' ? compactPercent(payload.rate) : null;
  const displayApr = payload && typeof payload.apr === 'string' ? compactPercent(payload.apr) : null;
  // Diagnostic (Ed 8/4 P0): name the first failing gate. A silent null here quietly strips the
  // page's rate line; this warn is what localizes a payload/validator drift in minutes.
  try {
    const _extra = unknownKeys(payload, topKeys);
    if (_extra.length) { console.warn('[effective-rate] unknown additive payload keys (tolerated):', _extra.join(',')); }
  } catch (e) { /* non-fatal */ }
  const gates: Array<[string, boolean]> = [
    ['topKeys', exactKeys(payload, topKeys.filter(k => k !== 'rates_checked'))],
    ['status', !!payload && payload.status === 'ok'],
    ['payload_version', !!payload && payload.payload_version === 'effective-revision-v1'],
    ['payload_source', !!payload && payload.payload_source === 'reviewed_effective_revision'],
    ['rate_set_name', !!payload && payload.rate_set_name === 'FreeRateUpdate As Low As Mortgage & Home Equity Rates'],
    ['bucket_match', !!payload && payload.purpose === bucket.purpose && payload.product === bucket.product],
    ['revision_id', !!payload && typeof payload.revision_id === 'string' && /^rates-\d{4}-\d{2}-\d{2}(?:-[a-z0-9-]+)?$/.test(payload.revision_id)],
    ['revision_sha256', !!payload && sha256(payload.revision_sha256)],
    ['generated_at', !!payload && typeof payload.generated_at === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:-04:00|-05:00)$/.test(payload.generated_at)],
    ['generated_at_et', !!payload && typeof payload.generated_at_et === 'string' && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2} (?:AM|PM) (?:EST|EDT)$/.test(payload.generated_at_et)],
    ['geo', !!payload && exactKeys(payload.geo, ['state', 'zip', 'city']) && payload.geo.state === 'PA' && payload.geo.zip === '19060' && payload.geo.city === null],
    ['scenario', !!payload && lockedScenario(payload.scenario)],
    ['display_values', displayRate !== null && displayApr !== null && !!payload && payload.display_rate === displayRate && payload.display_apr === displayApr && payload.display_pair === `${displayRate}% ${displayApr} APR`],
    ['warnings', !!payload && Array.isArray(payload.warnings) && payload.warnings.length === 0],
    ['offer_sha256', !!payload && sha256(payload.offer_sha256)],
    ['offer', !!payload && exactPublicOffer(payload.offer, payload, bucket, lineOfCredit)],
    ['disclosure', !!payload && exactDisclosure(payload.disclosure, payload, bucket, expectedDisclosureOrigin)],
  ];
  const failed = gates.filter(g => !g[1]).map(g => g[0]);
  if (!failed.length && payload && payload.offer) { warnOnScenarioDrift(payload.offer, payload); }
  if (failed.length) {
    try { console.warn('[effective-rate] payload rejected at gate(s):', failed.join(','), 'bucket:', bucket.purpose + '.' + bucket.product); } catch (e) { /* non-fatal */ }
  }
  if (!exactKeys(payload, topKeys.filter(k => k !== 'rates_checked'))
    || payload.status !== 'ok'
    || payload.payload_version !== 'effective-revision-v1'
    || payload.payload_source !== 'reviewed_effective_revision'
    || payload.rate_set_name !== 'FreeRateUpdate As Low As Mortgage & Home Equity Rates'
    || payload.purpose !== bucket.purpose
    || payload.product !== bucket.product
    || typeof payload.revision_id !== 'string'
    || !/^rates-\d{4}-\d{2}-\d{2}(?:-[a-z0-9-]+)?$/.test(payload.revision_id)
    || !sha256(payload.revision_sha256)
    || typeof payload.generated_at !== 'string'
    || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:-04:00|-05:00)$/.test(payload.generated_at)
    || typeof payload.generated_at_et !== 'string'
    || !/^\d{4}-\d{2}-\d{2} \d{2}:\d{2} (?:AM|PM) (?:EST|EDT)$/.test(payload.generated_at_et)
    || !exactKeys(payload.geo, ['state', 'zip', 'city'])
    || payload.geo.state !== 'PA'
    || payload.geo.zip !== '19060'
    || payload.geo.city !== null
    || !lockedScenario(payload.scenario)
    || displayRate === null
    || displayApr === null
    || payload.display_rate !== displayRate
    || payload.display_apr !== displayApr
    || payload.display_pair !== `${displayRate}% ${displayApr} APR`
    || !Array.isArray(payload.warnings)
    || payload.warnings.length !== 0
    || !sha256(payload.offer_sha256)
    || !exactPublicOffer(payload.offer, payload, bucket, lineOfCredit)
    || !exactDisclosure(payload.disclosure, payload, bucket, expectedDisclosureOrigin)) {
    return null;
  }
  // Ed 8/6 (conversion): the rates owner's freshness stamp rides into the headline when it
  // matches the exact expected phrasing -- anything else is silently dropped (never junk on page).
  // CR #1198: real clock values only (1-12 hours, 00-59 minutes), and the check's ISO date rides
  // along so renderers can verify "today" is still true AT PAINT TIME (window-cached responses
  // re-painted after midnight must drop the stamp).
  const _rc = payload.rates_checked;
  const ratesCheckedTimestamp = strictIsoEpochSeconds(_rc && _rc.iso);
  const ratesChecked = _rc && typeof _rc.display === 'string'
    && /^Rates checked today at (1[0-2]|[1-9]):[0-5][0-9] [AP]M ET$/.test(_rc.display)
    && ratesCheckedTimestamp !== undefined
    ? _rc.display : undefined;
  const ratesCheckedIso = ratesChecked ? _rc.iso : undefined;
  return {
    status: 'ok',
    effective: true,
    rates_checked_display: ratesChecked,
    rates_checked_iso: ratesCheckedIso,
    rates_checked_timestamp: ratesChecked ? ratesCheckedTimestamp : undefined,
    // FRU-24965: a valid check of ANY day. Hope's line under the box reads "Real-Time Rate Checks", which names no time, so it
    // shows for an earlier day's check too, as the original page does. The stamp fields above keep their same-day rule.
    rates_checked_any_iso: ratesCheckedTimestamp !== undefined ? _rc.iso : undefined,
    payload_version: 'effective-revision-v1',
    payload_source: 'reviewed_effective_revision',
    revision_id: payload.revision_id,
    revision_sha256: payload.revision_sha256,
    offer_sha256: payload.offer_sha256,
    purpose: bucket.purpose,
    product: bucket.product,
    rate: payload.rate,
    apr: payload.apr,
    points: payload.points,
    display_rate: displayRate,
    display_apr: displayApr,
    display_pair: payload.display_pair,
    scenario: {
      state: payload.scenario.state,
      zip: payload.scenario.zip,
      property_value: payload.scenario.property_value,
      loan_amount: payload.scenario.loan_amount,
      cash_out_amount: payload.scenario.cash_out_amount,
      credit_score: payload.scenario.credit_score,
      credit_tier: payload.scenario.credit_tier,
    },
    disclosure: {
      type: payload.disclosure.type,
      option: payload.disclosure.option,
      url: payload.disclosure.url,
      context: payload.disclosure.context,
      version_id: payload.disclosure.version_id,
      content_sha256: payload.disclosure.content_sha256,
      bundle_resource_id: payload.disclosure.bundle_resource_id,
      bundle_sha256: payload.disclosure.bundle_sha256,
      public_offer_sha256: payload.disclosure.public_offer_sha256,
    },
  } as Rate;
}

export function unavailableEffectiveRate(bucket: AdvertisedRateBucket, reason: string): Rate {
  return {
    status: 'unavailable',
    effective: true,
    purpose: bucket.purpose,
    product: bucket.product,
    rate: null,
    apr: null,
    display_rate: null,
    display_apr: null,
    display_pair: null,
    reason,
  };
}

export function unscopedEffectiveRate(reason: string): Rate {
  return {
    status: 'unavailable',
    effective: true,
    rate: null,
    apr: null,
    display_rate: null,
    display_apr: null,
    display_pair: null,
    reason,
  };
}

export function rateDisplayValues(rate: Rate | null): { rate: string | number; apr: string | number } | null {
  if (!rate) { return null; }
  if (rate.effective) {
    return rate.status === 'ok' && rate.display_rate != null && rate.display_apr != null
      ? { rate: rate.display_rate, apr: rate.display_apr }
      : null;
  }
  return rate.rate != null && rate.apr != null && String(rate.rate).trim() !== '' && String(rate.apr).trim() !== ''
    ? { rate: rate.rate, apr: rate.apr }
    : null;
}
